import { PageProps } from '@/types/PageProps';
import { vehicleHistoryRoutes } from '../route';
import { DefaultRouteTabs, LicensePlateFormField } from '@/components/_common';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { isAppPortocar } from '@/index';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Row,
  Spin,
  Typography,
} from 'antd';
import { useVehiclesHistoryPorto } from '@/core/domain/workorder/VehiclesHistory/VehiclesHistory.hooks';
import { useState } from 'react';
import { VehicleHistoryPortoRequest } from '@/core/domain/workorder/VehiclesHistory/VehiclesHistory.types';
import { useDebounce } from '@/hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import VehicleHistoryWorkOrderListPorto from '@/components/History/VehiclesHistory/VehicleHistoryWorkOrderListPorto';

interface FormValues {
  licensePlate?: string;
  search?: string;
}

export default function VehiclePlateHistoryPage({ route }: PageProps) {
  const intl = useIntl();

  const [form] = Form.useForm<FormValues>();
  const [queryParams, setQueryParams] = useState<VehicleHistoryPortoRequest>();

  const queryDebounce = useDebounce(queryParams);
  const vehiclesHistoryPorto = useVehiclesHistoryPorto(queryDebounce);

  const vehiclesHistoryData = flatInfiniteData(vehiclesHistoryPorto.data);
  const vehiclesHistoryByPorto =
    vehiclesHistoryPorto.data?.pages?.[0].vehicleHistoryByPorto;

  const newRoute = {
    ...route.parent,
    routes: route.parent.routes.filter((route) => {
      const isAllRoute = vehicleHistoryRoutes.includes(route.key);
      if (route.key === 'plateHistory') return isAllRoute && isAppPortocar;
      return isAllRoute;
    }),
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs route={newRoute} persistSearchParams={false}>
        <Form form={form}>
          <div
            style={{
              maxWidth: 1200,
              margin: 'auto',
            }}
          >
            <Row gutter={[12, 12]} justify="center">
              <Col span={8}>
                <Row gutter={[12, 12]}>
                  <Col span={24}>
                    <Typography.Title level={4} style={{ margin: 0 }}>
                      Histórico por placa
                    </Typography.Title>
                  </Col>

                  <Col span={24}>
                    <LicensePlateFormField
                      name="licensePlate"
                      label="Digite a placa..."
                      required={false}
                      maskedInputProps={{
                        onChange: ({ target: { value } }) => {
                          setQueryParams((prev) => ({
                            ...prev,
                            licensePlate: value,
                            search: undefined,
                          }));

                          form.setFieldValue('search', undefined);
                        },
                        autoFocus: true,
                      }}
                    />
                  </Col>

                  {vehiclesHistoryPorto.isLoading && (
                    <Col span={24} style={{ textAlign: 'center' }}>
                      <Spin tip="Carregando informações..." />
                    </Col>
                  )}
                </Row>
              </Col>

              {!!vehiclesHistoryByPorto && (
                <Col span={24}>
                  <Card>
                    <Typography.Text strong style={{ margin: 0, fontSize: 20 }}>
                      {queryParams?.licensePlate}
                    </Typography.Text>

                    <Descriptions
                      layout="vertical"
                      bordered
                      column={{ xs: 1, sm: 2, md: 4 }}
                    >
                      <Descriptions.Item label="Modelo">
                        {vehiclesHistoryByPorto.vehicleModel || '-'}
                      </Descriptions.Item>

                      <Descriptions.Item label="Combustível">
                        {vehiclesHistoryByPorto.vehicleFuel || '-'}
                      </Descriptions.Item>

                      <Descriptions.Item label="Ano de Fabricação/Modelo">
                        {vehiclesHistoryByPorto.vehicleYearFabrication}/
                        {vehiclesHistoryByPorto.vehicleYearModel}
                      </Descriptions.Item>

                      <Descriptions.Item label="Frota">
                        {vehiclesHistoryByPorto.vehicleFleet || '-'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              )}

              {!!vehiclesHistoryData.length && (
                <>
                  <Col span={24}>
                    <Typography.Title
                      level={4}
                      style={{ margin: 0, marginTop: 24 }}
                    >
                      Histórico de Ordens de Serviço
                    </Typography.Title>
                  </Col>

                  <Col span={24}>
                    <VehicleHistoryWorkOrderListPorto
                      data={vehiclesHistoryData}
                    />
                  </Col>
                </>
              )}

              {vehiclesHistoryPorto.hasNextPage && (
                <Col span={24} style={{ textAlign: 'center', marginTop: 12 }}>
                  <Button
                    type="primary"
                    onClick={() => vehiclesHistoryPorto.fetchNextPage()}
                    disabled={vehiclesHistoryPorto.isFetchingNextPage}
                  >
                    {vehiclesHistoryPorto.isFetchingNextPage
                      ? 'Carregando...'
                      : 'Carregar mais'}
                  </Button>
                </Col>
              )}
            </Row>
          </div>
        </Form>
      </DefaultRouteTabs>
    </>
  );
}
